import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API_BASE_URL as API } from '../config/api';
import {
  Music, ShoppingCart, Check, Shield, Download, FileAudio,
  Layers, ArrowLeft, Loader2, Play, Heart, ExternalLink,
  CheckCircle, AlertCircle, Clock
} from 'lucide-react';

const LICENSE_ICONS = {
  basic: FileAudio,
  premium: Download,
  exclusive: Layers,
};

const LICENSE_COLORS = {
  basic: 'border-[#007AFF]/30 hover:border-[#007AFF]/60',
  premium: 'border-[#A78BFA]/30 hover:border-[#A78BFA]/60',
  exclusive: 'border-[#FBBF24]/30 hover:border-[#FBBF24]/60',
};

const LICENSE_ACCENTS = {
  basic: 'text-[#007AFF]',
  premium: 'text-[#A78BFA]',
  exclusive: 'text-[#FBBF24]',
};

export default function BeatStorePage() {
  const { trackId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, getAuthHeaders } = useAuth();

  const [trackData, setTrackData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState('basic');

  // Success page handling
  const sessionId = searchParams.get('session_id');
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [polling, setPolling] = useState(false);

  // Fetch track data
  useEffect(() => {
    if (sessionId) {
      // We're on the success page
      setPolling(true);
      return;
    }
    if (!trackId) return;

    const fetchTrack = async () => {
      try {
        const res = await fetch(`${API}/api/beat-store/track/${trackId}`);
        if (res.ok) {
          setTrackData(await res.json());
        } else {
          toast.error('Track not found');
        }
      } catch (err) {
        console.error(err);
        toast.error('Failed to load track');
      } finally {
        setLoading(false);
      }
    };
    fetchTrack();
  }, [trackId, sessionId]);

  // Poll payment status
  useEffect(() => {
    if (!polling || !sessionId) return;
    let attempts = 0;
    const maxAttempts = 10;

    const poll = async () => {
      try {
        const res = await fetch(`${API}/api/beat-store/purchase/status/${sessionId}`, {
          headers: getAuthHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          if (data.payment_status === 'paid') {
            setPaymentStatus('success');
            setPolling(false);
            return;
          } else if (data.status === 'expired') {
            setPaymentStatus('expired');
            setPolling(false);
            return;
          }
        }
      } catch (err) {
        console.error('Poll error:', err);
      }

      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(poll, 2000);
      } else {
        setPaymentStatus('timeout');
        setPolling(false);
      }
    };

    poll();
  }, [polling, sessionId, getAuthHeaders]);

  const handlePurchase = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    setPurchasing(true);
    try {
      const res = await fetch(`${API}/api/beat-store/purchase`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          track_id: trackId,
          license_type: selectedLicense,
          origin_url: window.location.origin,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.checkout_url;
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Purchase failed');
      }
    } catch (err) {
      toast.error('Failed to create checkout');
    } finally {
      setPurchasing(false);
    }
  };

  // Success view
  if (sessionId) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-4" data-testid="beat-purchase-success">
        <Card className="bg-[#141414] border-white/10 rounded-2xl p-8 max-w-md w-full text-center">
          {polling && (
            <>
              <Loader2 className="w-12 h-12 animate-spin text-[#007AFF] mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">Processing Payment...</h2>
              <p className="text-sm text-zinc-400">Confirming your purchase. Please wait.</p>
            </>
          )}
          {paymentStatus === 'success' && (
            <>
              <div className="w-16 h-16 rounded-full bg-[#34D399]/15 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-[#34D399]" />
              </div>
              <h2 className="text-xl font-bold mb-2">Purchase Complete!</h2>
              <p className="text-sm text-zinc-400 mb-6">Your license has been activated. Check your purchases for download access.</p>
              <div className="flex gap-3 justify-center">
                <Button onClick={() => navigate('/beat-store/my-purchases')} className="bg-[#007AFF] hover:bg-[#0066DD]" data-testid="view-purchases-btn">
                  View Purchases
                </Button>
                <Button variant="outline" onClick={() => navigate('/soundstage-spotlight')} className="border-white/10" data-testid="back-to-feed-btn">
                  Back to Feed
                </Button>
              </div>
            </>
          )}
          {paymentStatus === 'expired' && (
            <>
              <AlertCircle className="w-12 h-12 text-[#FF3B30] mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">Payment Expired</h2>
              <p className="text-sm text-zinc-400 mb-4">Your session expired. Please try again.</p>
              <Button onClick={() => navigate(-1)} className="bg-[#007AFF]">Try Again</Button>
            </>
          )}
          {paymentStatus === 'timeout' && (
            <>
              <Clock className="w-12 h-12 text-[#FBBF24] mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">Still Processing</h2>
              <p className="text-sm text-zinc-400 mb-4">We're still confirming your payment. Check your email for confirmation.</p>
              <Button onClick={() => navigate('/soundstage-spotlight')} className="bg-[#007AFF]">Return Home</Button>
            </>
          )}
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#007AFF]" />
      </div>
    );
  }

  if (!trackData) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
        <p className="text-zinc-400">Track not found</p>
      </div>
    );
  }

  const { track, seller, licenses } = trackData;
  const selectedPkg = licenses[selectedLicense];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="beat-store-page">
      {/* Header */}
      <div className="sticky top-0 z-20 backdrop-blur-xl bg-[#0A0A0A]/70 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-zinc-400 hover:text-white" data-testid="store-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-lg font-bold">Beat Store</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Track Info — Left */}
          <div className="lg:col-span-2 space-y-4">
            <div className="aspect-square rounded-2xl bg-[#141414] border border-white/10 overflow-hidden">
              {track.cover_art ? (
                <img src={track.cover_art} alt={track.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Music className="w-20 h-20 text-zinc-700" />
                </div>
              )}
            </div>
            <div>
              <h2 className="text-2xl font-bold tracking-tight" data-testid="track-title">{track.title}</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm text-zinc-400">by</span>
                <span className="text-sm font-medium">{seller.name}</span>
                {seller.is_verified && <Check className="w-3.5 h-3.5 text-[#007AFF]" />}
              </div>
              {track.genre && <Badge className="mt-2 bg-white/10 border-0 text-zinc-300 text-xs">{track.genre}</Badge>}
              <div className="flex items-center gap-4 mt-3 text-xs text-zinc-500">
                {track.bpm && <span>{track.bpm} BPM</span>}
                {track.key && <span>Key: {track.key}</span>}
                <span><Play className="w-3 h-3 inline mr-1" />{(track.plays || 0).toLocaleString()}</span>
                <span><Heart className="w-3 h-3 inline mr-1" />{(track.likes || 0).toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Licenses — Right */}
          <div className="lg:col-span-3 space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-[0.15em] text-zinc-400">Select License</h3>

            <div className="space-y-3">
              {Object.entries(licenses).map(([key, lic]) => {
                const Icon = LICENSE_ICONS[key] || FileAudio;
                const isSelected = selectedLicense === key;
                return (
                  <Card
                    key={key}
                    onClick={() => setSelectedLicense(key)}
                    className={`bg-[#141414] rounded-xl p-5 cursor-pointer transition-all duration-200 border-2 ${
                      isSelected ? LICENSE_COLORS[key]?.replace('hover:', '') + ' bg-white/[0.02]' : 'border-white/5 hover:border-white/15'
                    }`}
                    data-testid={`license-${key}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg ${isSelected ? 'bg-white/10' : 'bg-white/5'} flex items-center justify-center`}>
                          <Icon className={`w-5 h-5 ${isSelected ? LICENSE_ACCENTS[key] : 'text-zinc-500'}`} />
                        </div>
                        <div>
                          <p className="font-semibold">{lic.name}</p>
                          <p className="text-xs text-zinc-500 mt-0.5">{lic.description}</p>
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {lic.includes?.map((item, i) => (
                              <span key={i} className="inline-flex items-center gap-1 text-[10px] text-zinc-400 bg-white/5 rounded-full px-2 py-0.5">
                                <Check className="w-2.5 h-2.5 text-[#34D399]" />{item}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="text-right shrink-0 ml-4">
                        <p className={`text-2xl font-bold ${LICENSE_ACCENTS[key]}`} style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                          ${lic.price.toFixed(2)}
                        </p>
                        {isSelected && (
                          <div className="w-5 h-5 rounded-full bg-[#007AFF] flex items-center justify-center mt-1 ml-auto">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Purchase Button */}
            <div className="pt-4 border-t border-white/10">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm text-zinc-400">Total</p>
                  <p className="text-3xl font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    ${selectedPkg?.price.toFixed(2)}
                  </p>
                </div>
                <div className="flex items-center gap-2 text-xs text-zinc-500">
                  <Shield className="w-3.5 h-3.5" />
                  Secure checkout via Stripe
                </div>
              </div>
              <Button
                onClick={handlePurchase}
                disabled={purchasing}
                className="w-full h-12 bg-[#007AFF] hover:bg-[#0066DD] text-white font-semibold text-base rounded-xl transition-all"
                data-testid="purchase-btn"
              >
                {purchasing ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" />Processing...</>
                ) : (
                  <><ShoppingCart className="w-4 h-4 mr-2" />Purchase {selectedPkg?.name}</>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
